import type { AudioClip, WarpMarker } from './types';

/**
 * The map from a clip's source time to its musical position.
 *
 * A clip that follows the tempo is a fixed number of beats of music. By default
 * those beats run evenly across the recording. Warp markers break that: each one
 * says "this moment in the audio happens on this beat", and between consecutive
 * markers the audio is stretched linearly to fit. That is how a performance that
 * drifts is pulled straight without disturbing the parts that were already
 * right.
 *
 * The clip's own start and end are always pinned — (0, 0) and
 * (durationSec, totalBeats) — and are not stored as markers. So an empty marker
 * list is exactly the even mapping, and adding markers cannot change how long
 * the clip is: the overall fit is the clip's source tempo, and markers correct
 * what happens inside it.
 */

export interface WarpPoint {
  sourceSec: number;
  beat: number;
}

/** The smallest gap allowed between two points, in either dimension. */
export const MIN_WARP_GAP_SEC = 0.02;
export const MIN_WARP_GAP_BEATS = 0.02;

/** Total quarter notes a following clip occupies. Null when it does not follow. */
export function warpTotalBeats(clip: AudioClip): number | null {
  if (clip.warpSourceBpm === null) return null;
  return (clip.durationSec * clip.warpSourceBpm) / 60;
}

/**
 * The clip's full warp curve: the pinned start, its markers in order, the pinned
 * end. Markers that are out of order, out of range, or too close to a neighbour
 * are dropped rather than allowed to make the map non-monotonic — a map that
 * went backwards would read the source backwards.
 */
export function warpCurve(clip: AudioClip): WarpPoint[] {
  const totalBeats = warpTotalBeats(clip);
  if (totalBeats === null || clip.durationSec <= 0) return [];

  const points: WarpPoint[] = [{ sourceSec: 0, beat: 0 }];
  const sorted = [...clip.warpMarkers].sort((a, b) => a.sourceSec - b.sourceSec || a.beat - b.beat);
  for (const marker of sorted) {
    const previous = points[points.length - 1];
    if (!Number.isFinite(marker.sourceSec) || !Number.isFinite(marker.beat)) continue;
    if (marker.sourceSec - previous.sourceSec < MIN_WARP_GAP_SEC) continue;
    if (marker.beat - previous.beat < MIN_WARP_GAP_BEATS) continue;
    if (clip.durationSec - marker.sourceSec < MIN_WARP_GAP_SEC) continue;
    if (totalBeats - marker.beat < MIN_WARP_GAP_BEATS) continue;
    points.push({ sourceSec: marker.sourceSec, beat: marker.beat });
  }
  points.push({ sourceSec: clip.durationSec, beat: totalBeats });
  return points;
}

/** Where a beat position falls in the source, in seconds from the clip's start. */
export function beatToSourceSec(curve: readonly WarpPoint[], beat: number): number {
  if (curve.length < 2) return 0;
  const last = curve[curve.length - 1];
  const clamped = Math.min(last.beat, Math.max(0, beat));
  for (let i = 1; i < curve.length; i++) {
    const a = curve[i - 1];
    const b = curve[i];
    if (clamped > b.beat && i < curve.length - 1) continue;
    const span = b.beat - a.beat;
    const progress = span <= 0 ? 0 : (clamped - a.beat) / span;
    return a.sourceSec + (b.sourceSec - a.sourceSec) * progress;
  }
  return last.sourceSec;
}

/** Which beat a moment in the source lands on. */
export function sourceSecToBeat(curve: readonly WarpPoint[], sourceSec: number): number {
  if (curve.length < 2) return 0;
  const last = curve[curve.length - 1];
  const clamped = Math.min(last.sourceSec, Math.max(0, sourceSec));
  for (let i = 1; i < curve.length; i++) {
    const a = curve[i - 1];
    const b = curve[i];
    if (clamped > b.sourceSec && i < curve.length - 1) continue;
    const span = b.sourceSec - a.sourceSec;
    const progress = span <= 0 ? 0 : (clamped - a.sourceSec) / span;
    return a.beat + (b.beat - a.beat) * progress;
  }
  return last.beat;
}

/** True when the clip's curve is anything other than the even mapping. */
export function hasWarpMarkers(clip: AudioClip): boolean {
  return clip.warpSourceBpm !== null && warpCurve(clip).length > 2;
}

/**
 * A clip's markers turned round with its audio.
 *
 * A marker is a pair — this moment of the source, this beat — and reversing a
 * clip reverses both. The moment becomes its distance from the far end of the
 * span, because that is where it now sits in the reversed audio; the beat
 * becomes its distance from the last one, because that is when it is now
 * heard. The pinned ends map onto each other, so the mirrored curve is exactly
 * the old one read backwards: a reversed warped clip is the time-reversal of
 * the forwards one, marker for marker. Applied twice it gives the markers back.
 */
export function mirrorWarpMarkers(clip: AudioClip): WarpMarker[] {
  const totalBeats = warpTotalBeats(clip);
  if (totalBeats === null || clip.warpMarkers.length === 0) return clip.warpMarkers;
  return clip.warpMarkers
    .map((marker) => ({
      ...marker,
      sourceSec: clip.durationSec - marker.sourceSec,
      beat: totalBeats - marker.beat,
    }))
    .sort((a, b) => a.sourceSec - b.sourceSec);
}

/**
 * Which beat a moment of source lands on, inside the clip or beyond it.
 *
 * Inside, the curve decides. Outside — a trim reaching into source the clip
 * was not playing — there is no curve to follow, so the clip's tempo does:
 * that is what "the same clip, a little longer" has to mean, and what the even
 * mapping would have given anyway.
 */
export function beatAtSource(clip: AudioClip, sourceSec: number): number {
  if (clip.warpSourceBpm === null) return 0;
  const beatsPerSec = clip.warpSourceBpm / 60;
  const totalBeats = clip.durationSec * beatsPerSec;
  if (sourceSec < 0) return sourceSec * beatsPerSec;
  if (sourceSec > clip.durationSec) return totalBeats + (sourceSec - clip.durationSec) * beatsPerSec;
  return sourceSecToBeat(warpCurve(clip), sourceSec);
}

/**
 * A clip's warp, cut to a slice of its source: `leadSec` dropped from the
 * front, `durationSec` kept, both in the order the clip is heard.
 *
 * Trimming or splitting a warped clip has to carry its markers into the new
 * region's coordinates — a marker left at its old offset would pin the wrong
 * moment of audio to the wrong beat, which is worse than losing it — and it
 * has to leave every moment of audio on the beat it was on. The second part is
 * the one that is not free. A following clip's length in beats is its source
 * length at its tempo, and once markers have bent the curve the slice that
 * remains is not that many beats: cut the stretched first second off a clip and
 * the remainder, refitted at the recorded tempo, ends early and every marker
 * lands short. So the slice takes the tempo that *keeps its beats where they
 * were* — the source it holds over the beats the curve gave that source — and
 * `warpSourceBpm` becomes a fit rather than a fact about the recording. That
 * is the only reading under which a trim changes nothing but the edge.
 *
 * A clip without markers is exactly the even mapping, and the even mapping
 * slices at the recorded tempo unchanged, so nothing is touched there.
 */
export function sliceWarp(
  clip: AudioClip,
  leadSec: number,
  durationSec: number,
): { warpSourceBpm: number | null; warpMarkers: WarpMarker[] } {
  if (clip.warpSourceBpm === null) return { warpSourceBpm: null, warpMarkers: clip.warpMarkers };
  if (clip.warpMarkers.length === 0 || durationSec <= 0) return { warpSourceBpm: clip.warpSourceBpm, warpMarkers: [] };
  const leadBeat = beatAtSource(clip, leadSec);
  const beats = beatAtSource(clip, leadSec + durationSec) - leadBeat;
  const warpMarkers: WarpMarker[] = [];
  for (const marker of clip.warpMarkers) {
    const sourceSec = marker.sourceSec - leadSec;
    if (sourceSec <= 0 || sourceSec >= durationSec) continue;
    warpMarkers.push({ ...marker, sourceSec, beat: marker.beat - leadBeat });
  }
  return { warpSourceBpm: beats > 0 ? (60 * beats) / durationSec : clip.warpSourceBpm, warpMarkers };
}
